import { ArrowSquareOutIcon, CaretRightIcon, EyeIcon, XIcon } from '@phosphor-icons/react'
import { mappedColors, typography } from '@pixee/design-system'
import * as Accordion from '@radix-ui/react-accordion'
import * as Dialog from '@radix-ui/react-dialog'
import * as Tabs from '@radix-ui/react-tabs'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { match, P } from 'ts-pattern'
import { useToastService } from '../../services/toast-service.tsx'
import { ApiError } from '../../utils/api-client/api-error'
import {
  useGetResultActivitySSE,
  useGetScanAnalysis,
  useGetScanFinding,
  useGetScanFindingResults,
  useGetScaResultArticle,
  useGetScaResultReport,
} from '../../utils/api-client/api-hooks.ts'
import {
  type FindingResponseWithoutEmbed,
  type FixResultResponse,
  type ResultsScaResultResponse,
  type ScaOutcome,
} from '../../utils/api-client/api-schemas.ts'
import { shouldShowScaConfetti } from '../../utils/confirmation-utils.ts'
import { getAnalysisMetadata, getFirstScaResultFromResults, getResultById } from '../../utils/finding-utils.ts'
import { joinCopySections } from '../../utils/text-utils.ts'
import { AnalysisActivityTab, type AnalysisActivityTabProps } from '../activity-feed/analysis-activity-tab.tsx'
import { AnalysisTransitionOverlay } from '../activity-feed/analysis-transition-overlay.tsx'
import { getOutcomeBannerCopyText, OutcomeBanner, type OutcomeBannerOutcome } from '../activity-feed/outcome-banner.tsx'
import { StatusNotification, type StatusNotificationStatus } from '../activity-feed/status-notification.tsx'
import { useActivityResultSelection } from '../activity-feed/use-activity-result-selection.ts'
import { useActivityTabBehavior } from '../activity-feed/use-activity-tab-behavior.ts'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { ProgressButton } from '../buttons/progress-button.tsx'
import { DrawerConfetti } from '../drawer-confetti.tsx'
import { ErrorBoundary } from '../error-boundaries/error-boundary'
import { OutcomeReasonDetails } from '../outcomes/outcome-reason-details.tsx'
import { isBlockedOrExcludedScaOutcome } from '../outcomes/outcome-utils.ts'
import { RenderedMarkdown } from '../rendered-markdown.tsx'
import * as scrollableTabBarStyles from '../scrollable-tab-bar/scrollable-tab-bar.css.ts'
import { ScrollableTabBar } from '../scrollable-tab-bar/scrollable-tab-bar.tsx'
import { Spinner } from '../spinner.tsx'
import { resolveVisibleTab, useDrawerContentDimensions } from './drawer-utils.ts'
import { FeedbackBar } from './feedback-bar.tsx'
import * as footerStyles from './floating-footer.css'
import { ResultNotFoundFallback } from './result-not-found-fallback.tsx'
import type { DependencyNodeDetail } from './sca-dependency-tree.tsx'
import * as styles from './sca-finding-drawer.css.ts'
import { useNavigateScanFindingDrawers } from './use-navigate-scan-finding-drawers.ts'

const ScaDependencyTree = lazy(() => import('./sca-dependency-tree.tsx').then(m => ({ default: m.ScaDependencyTree })))
const ScaDependencyLegend = lazy(() =>
  import('./sca-dependency-tree.tsx').then(m => ({ default: m.ScaDependencyLegend }))
)

type FindingScaDrawerTab = 'summary' | 'full-report' | 'analysis-activity' | 'dependency-graph'

type DependencyChainNode = {
  name: string
  version?: string | null
  node_type: 'root' | 'direct' | 'transitive' | 'vulnerable' | (string & {})
}

// Extract the <!--dep-graph ... --> appendix block when the backend embedded it.
// Returns the raw block content, or null if absent (old report format).
export function extractAppendix(markdown: string): string | null {
  const m = markdown.match(/<!--dep-graph\n([\s\S]*?)\n-->/)
  return m ? m[1]! : null
}

// Strip the appendix comment from markdown before rendering — it is data for the
// graph UI, not prose, and renders as visible raw text in markdown renderers.
export function stripAppendix(markdown: string): string {
  return markdown.replace(/\n*<!--dep-graph[\s\S]*?-->/g, '')
}

// Parse per-TID exploitability verdicts.
// Handles two formats:
//   Appendix chains:  - `root@v -> tid@v -> pkg@v` — exploitable   (TID = 2nd segment)
//   Overview bullets: - `grunt-concurrent@2.3.1` — **exploitable**  (single package)
export function parseVerdicts(markdown: string): Map<string, string> {
  const map = new Map<string, string>()
  // Explicit alternation for verdict strings — lazy char-class quantifiers stop too early
  // for multi-word verdicts like "not exploitable" and always produce wrong captures.
  const pattern = /^-\s+`([^`]+)`\s*[—–]+\s*\**(exploitable|not exploitable|inconclusive|not evaluated)\**/gm
  let m: RegExpExecArray | null
  while ((m = pattern.exec(markdown)) !== null) {
    const content = m[1]!.trim()
    const verdict = m[2]!.trim()
    const segments = content.split(/\s*->\s*/)
    // Chain format: key off the TID (second segment); single-package format: key off the whole thing
    const pkg = segments.length >= 2 ? segments[1]!.trim() : content
    map.set(pkg, verdict)
    const atIdx = pkg.lastIndexOf('@')
    if (atIdx > 0) map.set(pkg.slice(0, atIdx), verdict)
  }
  return map
}

export function parseChainsFromText(text: string): DependencyChainNode[][] {
  const chains: DependencyChainNode[][] = []
  const pattern = /`([^`]+(?:\s*->\s*[^`]+)+)`/g
  let m: RegExpExecArray | null
  while ((m = pattern.exec(text)) !== null) {
    const parts = m[1]!.trim().split(/\s*->\s*/)
    if (parts.length < 2) continue
    const chain: DependencyChainNode[] = parts.map((part, i) => {
      const atIdx = part.lastIndexOf('@')
      const name = atIdx > 0 ? part.slice(0, atIdx) : part
      const version = atIdx > 0 ? part.slice(atIdx + 1) : undefined
      const node_type = i === 0 ? 'root' : i === parts.length - 1 ? 'vulnerable' : i === 1 ? 'direct' : 'transitive'
      return { name, version, node_type }
    })
    const key = chain.map(n => n.name).join('>')
    if (!chains.some(c => c.map(n => n.name).join('>') === key)) chains.push(chain)
  }
  return chains
}

function parseDependencyChains(markdown: string): DependencyChainNode[][] {
  // When the backend appendix is present, use it exclusively — it is authoritative
  // and has consistent root names. Falling back to the full report risks mixing
  // chain strings from LLM-generated text that use different root identifiers,
  // which causes a spurious synthetic root node in the graph.
  const appendix = extractAppendix(markdown)
  return parseChainsFromText(appendix ?? markdown)
}

// #region Wrapper

export type ScaFindingDrawerProps = {
  scanId: string
}

export const ScaFindingDrawer: React.FC<ScaFindingDrawerProps> = ({ scanId }) => {
  const { findingTriageFindingId, resultId, onClose, onOpenFindingFixDrawer, onNavigateToResult, isSca } =
    useNavigateScanFindingDrawers({
      scanId,
    })

  const isOpen = !!findingTriageFindingId && isSca
  const props = useScaFindingDrawerBase({
    scanId: isOpen ? scanId : undefined,
    findingId: isOpen ? findingTriageFindingId : undefined,
    resultId: isOpen ? resultId : undefined,
    onClose,
    onOpenFindingFixDrawer,
    onNavigateToResult,
  })

  if (!isOpen) return null

  return <ScaFindingDrawerPure {...props} />
}

// #endregion

// #region Hook

type UseScaFindingDrawerBaseOptions = {
  scanId?: string
  findingId?: string
  resultId?: string
  onClose: () => unknown
  onOpenFindingFixDrawer: (findingId: string | undefined, resultId?: string) => unknown
  onNavigateToResult?: (resultId: string) => unknown
}

export function useScaFindingDrawerBase({
  scanId,
  findingId,
  resultId,
  onClose,
  onOpenFindingFixDrawer,
  onNavigateToResult,
}: UseScaFindingDrawerBaseOptions) {
  const { data: selectedFinding, isSuccess: isFindingSuccess } = useGetScanFinding({
    scanId,
    findingId,
  })

  // Fetch results separately - the embed parameter on the finding endpoint doesn't work
  const { data: results } = useGetScanFindingResults({
    scanId,
    findingId,
    representative: !resultId,
    refetchInterval: findingId ? 1000 : undefined,
  })

  // Representative results exclude in-progress results, so fetch all results
  // separately for live detection during re-analysis.
  const { data: allResults } = useGetScanFindingResults({
    scanId,
    findingId,
    representative: false,
    enabled: !resultId,
    refetchInterval: findingId ? 1000 : undefined,
  })

  // Extract the SCA result - either by resultId or the first completed representative result
  const scaResult = resultId ? getResultById({ results, resultId, type: 'sca' }) : getFirstScaResultFromResults(results)

  // Only link the fix from the same analysis as the SCA result. The
  // representative-results endpoint selects the representative of each type
  // independently, so the representative SCA result and fix can originate from
  // different analyses; surfacing such a fix would link an unrelated fix
  // (ISS-6418). When the SCA result's own analysis produced no fix, show none.
  const fixResult = scaResult
    ? results?._embedded?.items.find(
        (r): r is FixResultResponse => r.type === 'fix' && r._links.analysis.href === scaResult._links.analysis.href
      )
    : undefined

  const isFixInProgress = !!fixResult && !fixResult.outcome

  const isRepresentative = !resultId
  const isResultNotFound = !!resultId && isFindingSuccess && !scaResult

  const { addToast } = useToastService()

  // Blocked/excluded findings have no article/report (the endpoints 404), so don't request them —
  // the Summary tab renders the outcome's own reason/details and the Full Report tab is hidden.
  const shouldFetchScaContent = !!scaResult?.id && !isBlockedOrExcludedScaOutcome(scaResult.outcome)

  // Fetch SCA article
  const { data: scaArticle } = useGetScaResultArticle({
    resultId: scaResult?.id,
    enabled: shouldFetchScaContent,
  })

  // Fetch SCA report
  const {
    data: scaReport,
    error: scaReportError,
    isError: isScaReportError,
  } = useGetScaResultReport({
    resultId: scaResult?.id,
    enabled: shouldFetchScaContent,
  })

  const isScaReportNotFound = isScaReportError && scaReportError instanceof ApiError && scaReportError.status === 404

  useEffect(() => {
    if (isScaReportError && !isScaReportNotFound) {
      onClose()
      addToast({
        message: <p>Failed loading SCA report: {scaReportError?.message ?? 'Unknown error'}</p>,
        variant: 'error',
      })
      if (scaReportError instanceof ApiError && scaReportError.bodyAsText) {
        addToast({
          message: <pre>{scaReportError.bodyAsText}</pre>,
          variant: 'error',
        })
      }
    }
  }, [isScaReportError, isScaReportNotFound, scaReportError, onClose, addToast])

  // Use all results for activity selection (falls back to representative while loading)
  const activityResults = resultId ? results : (allResults ?? results)
  const { activeResultId, isLive: hasInProgressResult } = useActivityResultSelection({
    results: activityResults,
    findingType: 'sca',
  })

  // isLive reflects the VIEWED result, not any result
  const isLive = !!scaResult && !scaResult.outcome

  const hasProcessedInitialResultsRef = useRef(false)
  const openedWithLiveAnalysisRef = useRef(false)
  // Wait specifically for the all-results query (no fallback) to avoid
  // capturing stale isLive=false from cached representative results.
  const liveDetectionResults = resultId ? results : allResults
  // Reset when results become unavailable (drawer closed or finding changed),
  // so the next open captures fresh initial state instead of stale values.
  if (liveDetectionResults === undefined) {
    hasProcessedInitialResultsRef.current = false
    openedWithLiveAnalysisRef.current = false
  }
  // Capture whether analysis was already live when results first loaded.
  // This distinguishes "drawer opened during analysis" (redirect to in-progress result)
  // from "analysis started while viewing drawer" (show notification instead).
  if (!hasProcessedInitialResultsRef.current && liveDetectionResults !== undefined) {
    hasProcessedInitialResultsRef.current = true
    openedWithLiveAnalysisRef.current = hasInProgressResult
  }

  // When the drawer opens with an in-progress analysis, redirect to the
  // in-progress result URL so it renders naturally (activity-only view,
  // then summary when complete, with correct fix association).
  //
  // Depend on all-results having loaded: `openedWithLiveAnalysisRef` is captured during the
  // render where all-results first loads, which need not change `activeResultId` (the
  // representative query can supply it first). Without this dep the effect runs once while
  // the ref is still false and never re-runs, leaving the drawer stuck in the tabbed view.
  const hasLoadedLiveDetectionResults = liveDetectionResults !== undefined
  useEffect(() => {
    if (openedWithLiveAnalysisRef.current && !resultId && activeResultId) {
      onNavigateToResult?.(activeResultId)
    }
  }, [resultId, activeResultId, onNavigateToResult, hasLoadedLiveDetectionResults])

  const queryClient = useQueryClient()
  const sseResultId = openedWithLiveAnalysisRef.current && !resultId ? activeResultId : scaResult?.id
  const activityStream = useGetResultActivitySSE(sseResultId, 'sca')

  const analysisHref = scaResult?._links.analysis.href
  const analysisId = analysisHref?.split('/').pop()
  const { data: analysis } = useGetScanAnalysis({ analysisId })

  const analysisMetadata = useMemo(
    () => getAnalysisMetadata({ analysis, resultCompleted: scaResult?.completed }),
    [analysis, scaResult?.completed]
  )

  useEffect(() => {
    if (activityStream.connectionStatus === 'finished' && analysisId) {
      queryClient.invalidateQueries({ queryKey: ['scan-analysis', analysisId] })
    }
  }, [activityStream.connectionStatus, analysisId, queryClient])

  const activityProps: AnalysisActivityTabProps | undefined = activityStream.isEndpointAvailable
    ? {
        events: activityStream.events,
        connectionStatus: activityStream.connectionStatus,
        error: activityStream.error,
        metadata: analysisMetadata,
        isLive,
      }
    : undefined

  const onViewActiveResult = activeResultId ? () => onNavigateToResult?.(activeResultId) : undefined

  const resolvedReport = isScaReportNotFound ? '' : scaReport
  const depGraphChains = useMemo(() => {
    const fromReport = resolvedReport ? parseDependencyChains(resolvedReport) : []
    if (fromReport.length) return fromReport
    return scaArticle ? parseDependencyChains(scaArticle) : []
  }, [resolvedReport, scaArticle])

  const depGraphVerdicts = useMemo(() => {
    const src = resolvedReport || scaArticle || ''
    return src ? parseVerdicts(src) : new Map<string, string>()
  }, [resolvedReport, scaArticle])

  return {
    finding: selectedFinding,
    scaResult,
    fixResult,
    isFixInProgress,
    isRepresentative,
    scaArticle: scaArticle ? stripAppendix(scaArticle) : scaArticle,
    scaReport: resolvedReport ? stripAppendix(resolvedReport) : resolvedReport,
    depGraphChains,
    depGraphVerdicts,
    activityProps,
    isLive,
    hasInProgressResult,
    onViewActiveResult,
    onClose,
    onOpenFindingFixDrawer,
    isResultNotFound,
  } satisfies Omit<
    ScaFindingDrawerPureProps,
    'defaultTab' | '_storyShowConfetti' | '_storyNotification' | '_storyShowConfirmation'
  >
}

// #endregion

// #region Pure Component

export type ScaFindingDrawerPureProps = {
  defaultTab?: FindingScaDrawerTab
  finding?: FindingResponseWithoutEmbed
  scaResult?: ResultsScaResultResponse
  fixResult?: FixResultResponse
  isFixInProgress: boolean
  isRepresentative: boolean
  scaArticle?: string
  scaReport?: string
  depGraphChains?: DependencyChainNode[][]
  depGraphVerdicts?: Map<string, string>
  isResultNotFound?: boolean
  activityProps?: AnalysisActivityTabProps
  isLive?: boolean
  hasInProgressResult?: boolean
  onViewActiveResult?: () => unknown
  onClose: () => unknown
  onOpenFindingFixDrawer: (findingId: string | undefined, resultId?: string) => unknown
  _storyShowConfetti?: boolean
  _storyNotification?: { status: StatusNotificationStatus; text: string; actionLabel?: string }
  _storyShowConfirmation?: boolean
}

export const ScaFindingDrawerPure: React.FC<ScaFindingDrawerPureProps> = ({
  defaultTab = 'summary',
  finding,
  scaResult,
  fixResult,
  isFixInProgress,
  scaArticle,
  scaReport,
  depGraphChains,
  depGraphVerdicts,
  isResultNotFound,
  activityProps,
  isLive,
  hasInProgressResult,
  onViewActiveResult,
  onClose,
  onOpenFindingFixDrawer,
  _storyShowConfetti,
  _storyNotification,
  _storyShowConfirmation,
}) => {
  const { drawerContentRef, contentDimensions } = useDrawerContentDimensions({ isEnabled: !!finding })
  const [storyConfetti, setStoryConfetti] = useState(false)

  const outcome = scaResult?.outcome ?? null

  const completedScaOutcome =
    outcome?.type === 'completed-sca'
      ? { type: 'completed-sca' as const, classification: outcome.classification, summary: outcome.summary }
      : undefined

  const scaOutcome: OutcomeBannerOutcome | undefined = outcome && outcome.type !== 'cancelled-sca' ? outcome : undefined

  const isBlockedOrExcluded = isBlockedOrExcludedScaOutcome(outcome)

  const isActivityTabVisible = !!activityProps && !isBlockedOrExcluded

  // Blocked/excluded findings have no report, so hide the Full Report tab (its content would only ever
  // be "No SCA report found"); the reason/details live on the Summary tab instead.
  const isFullReportTabVisible = !isBlockedOrExcluded

  const isDependencyGraphTabVisible = !isBlockedOrExcluded && !!scaResult?.outcome && !!depGraphChains?.length

  // The copy button copies whatever the tab shows. For blocked/excluded that's the classification
  // plus the reason/details (there's no report); for everything else it's the full report markdown.
  // The classification prefix mirrors the triage drawer's summary copy text.
  const scaCopyText = isBlockedOrExcluded
    ? joinCopySections(getOutcomeBannerCopyText(outcome), outcome.reason, outcome.details)
    : scaReport

  const isInProgress = !!isLive

  const {
    selectedTab: hookSelectedTab,
    handleTabChange,
    notification: hookNotification,
    switchToActivityTab,
    showConfirmation: hookShowConfirmation,
    showConfetti,
    onConfettiComplete,
  } = useActivityTabBehavior<FindingScaDrawerTab>({
    isLive: isLive ?? false,
    connectionStatus: activityProps?.connectionStatus,
    defaultTab: isInProgress ? 'analysis-activity' : defaultTab,
    activityTab: 'analysis-activity',
    isActivityTabVisible,
    shouldShowConfetti: _storyShowConfetti === undefined ? shouldShowScaConfetti(completedScaOutcome) : false,
  })

  const inProgressNotification =
    hasInProgressResult && !isLive ? { status: 'in-progress' as const, text: 'Analysis in progress' } : undefined
  const notification = _storyNotification ?? inProgressNotification ?? hookNotification
  const notificationActionLabel = _storyNotification !== undefined ? _storyNotification.actionLabel : 'View'
  const notificationAction = notificationActionLabel
    ? () => {
        switchToActivityTab()
        onViewActiveResult?.()
      }
    : undefined
  const showConfirmation = _storyShowConfirmation ?? hookShowConfirmation
  const rawSelectedTab = _storyShowConfirmation ? ('analysis-activity' as FindingScaDrawerTab) : hookSelectedTab
  // Built from the same booleans that gate the Tabs.Triggers, so trigger visibility and the
  // fallback below share one source of truth. Summary is always present.
  const visibleTabs: FindingScaDrawerTab[] = [
    'summary',
    ...(isFullReportTabVisible ? (['full-report'] as const) : []),
    ...(isActivityTabVisible ? (['analysis-activity'] as const) : []),
    ...(isDependencyGraphTabVisible ? (['dependency-graph'] as const) : []),
  ]
  const selectedTab = resolveVisibleTab({ selectedTab: rawSelectedTab, visibleTabs, fallbackTab: 'summary' })
  const showActivityOnlyView = isInProgress || showConfirmation

  useEffect(() => {
    if (isInProgress) {
      switchToActivityTab()
    }
  }, [isInProgress, switchToActivityTab])

  const AUTO_NAVIGATE_TO_FIX_DELAY_MS = 2000
  const [showFixTransitionOverlay, setShowFixTransitionOverlay] = useState(false)
  const prevShowConfirmationRef = useRef(showConfirmation)
  useEffect(() => {
    const wasConfirming = prevShowConfirmationRef.current
    prevShowConfirmationRef.current = showConfirmation

    if (wasConfirming && !showConfirmation && isFixInProgress && fixResult && finding) {
      const showOverlayTimer = setTimeout(() => {
        setShowFixTransitionOverlay(true)
      }, AUTO_NAVIGATE_TO_FIX_DELAY_MS)
      const navigateTimer = setTimeout(() => {
        setShowFixTransitionOverlay(false)
        onOpenFindingFixDrawer(finding.id, fixResult.id)
      }, AUTO_NAVIGATE_TO_FIX_DELAY_MS * 2)
      return () => {
        clearTimeout(showOverlayTimer)
        clearTimeout(navigateTimer)
      }
    }
  }, [showConfirmation])

  useEffect(() => {
    if (_storyShowConfetti) {
      const timer = setTimeout(() => setStoryConfetti(true), 300)
      return () => clearTimeout(timer)
    }
  }, [_storyShowConfetti])

  if (isResultNotFound) {
    return (
      <Dialog.Root open={true} modal={false}>
        <Dialog.Portal>
          <Dialog.Content
            className={styles.content}
            onEscapeKeyDown={onClose}
            onPointerDownOutside={event => event.preventDefault()}
            onInteractOutside={event => event.preventDefault()}
          >
            <Dialog.Close asChild className={styles.closeButton}>
              <button aria-label="Close" onClick={onClose}>
                <XIcon className={styles.closeButtonIcon} size={24} weight="light" />
              </button>
            </Dialog.Close>
            <ResultNotFoundFallback />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    )
  }

  if (!finding) {
    return null
  }

  const hasFeedbackBar = !!scaResult?.id && !isLive && !showActivityOnlyView
  const hasFloatingFooter = hasFeedbackBar

  return (
    <Dialog.Root open={true} modal={false}>
      <Dialog.Portal>
        <Dialog.Content
          ref={drawerContentRef}
          className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
          data-has-floating-footer={hasFloatingFooter ? true : undefined}
          onEscapeKeyDown={onClose}
          onPointerDownOutside={event => event.preventDefault()}
          onInteractOutside={event => event.preventDefault()}
        >
          <Dialog.Close asChild className={styles.closeButton}>
            <button aria-label="Close" onClick={onClose}>
              <XIcon className={styles.closeButtonIcon} size={24} weight="light" />
            </button>
          </Dialog.Close>
          <ErrorBoundary fallbackTitle="Failed to display SCA details">
            <FindingDetails finding={finding} />
            {match({ showActivityOnlyView })
              .with({ showActivityOnlyView: true }, () => (
                <div className={styles.tabContainer}>
                  <div>
                    {activityProps && (
                      <AnalysisActivityTab
                        {...activityProps}
                        progressLabel="SCA analysis"
                        isLive={isLive ?? false}
                        overlay={
                          showConfirmation ? <AnalysisTransitionOverlay message="Rendering SCA result" /> : undefined
                        }
                      />
                    )}
                  </div>
                  {showFixTransitionOverlay && (
                    <AnalysisTransitionOverlay message="Opening fix analysis" ariaLabel="Fix transition" />
                  )}
                </div>
              ))
              .otherwise(() => (
                <ScaResultTabs
                  selectedTab={selectedTab}
                  onTabChange={handleTabChange}
                  finding={finding}
                  fixResult={fixResult}
                  isFixInProgress={isFixInProgress}
                  onOpenFindingFixDrawer={onOpenFindingFixDrawer}
                  isActivityTabVisible={isActivityTabVisible}
                  isFullReportTabVisible={isFullReportTabVisible}
                  isDependencyGraphTabVisible={isDependencyGraphTabVisible}
                  depGraphChains={depGraphChains}
                  depGraphVerdicts={depGraphVerdicts}
                  activityProps={activityProps}
                  notification={notification}
                  notificationActionLabel={notificationActionLabel}
                  onNotificationAction={notificationAction}
                  outcome={outcome}
                  scaOutcome={scaOutcome}
                  scaArticle={scaArticle}
                  scaReport={scaReport}
                  scaCopyText={scaCopyText}
                  isLive={isLive ?? false}
                  showConfirmation={showConfirmation}
                  showFixTransitionOverlay={showFixTransitionOverlay}
                />
              ))}
            {(showConfetti || storyConfetti) && contentDimensions.width > 0 && (
              <DrawerConfetti
                width={contentDimensions.width}
                height={contentDimensions.height}
                onComplete={() => {
                  onConfettiComplete()
                  setStoryConfetti(false)
                }}
              />
            )}
          </ErrorBoundary>
        </Dialog.Content>
        {hasFeedbackBar && <FeedbackBar resultId={scaResult.id} resultType="sca" drawerContentRef={drawerContentRef} />}
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// #endregion

// #region Sub-components

const FindingDetails: React.FC<{
  finding: FindingResponseWithoutEmbed
}> = ({ finding }) => {
  const severityLabel = finding.severity?.label ?? 'Unknown'

  return (
    <Dialog.Description className={styles.header} aria-label="Finding Details" asChild>
      <section>
        <Dialog.Title className={styles.title}>
          {finding.title} <span className={styles.scaAnalysisTag}>SCA Analysis</span>
        </Dialog.Title>
        <div className={styles.metadata}>
          <div className={styles.metadataColumn}>
            <div className={styles.metadataItem}>
              <label id={`cve-label-${finding.id}`} className={styles.metadataItemLabel}>
                CVE
              </label>
              <span className={styles.metadataItemValue} aria-labelledby={`cve-label-${finding.id}`}>
                {finding.rule || 'N/A'}
              </span>
            </div>
            <div className={styles.metadataItem}>
              <label id={`severity-score-label-${finding.id}`} className={styles.metadataItemLabel}>
                Severity
              </label>
              <span className={styles.severityMetadataItemValue} aria-labelledby={`severity-score-label-${finding.id}`}>
                {severityLabel}
              </span>
            </div>
          </div>
          <div className={styles.metadataColumn}>
            <div className={styles.metadataItem}>
              <label id={`finding-link-label-${finding.id}`} className={styles.metadataItemLabel}>
                Finding
              </label>
              <span className={styles.metadataItemValue} aria-labelledby={`finding-link-label-${finding.id}`}>
                {finding.html_url ? (
                  <a href={finding.html_url} target="_blank" rel="noopener noreferrer" className={styles.findingLink}>
                    {finding.id}
                    <ArrowSquareOutIcon size={12} weight="light" />
                  </a>
                ) : (
                  finding.id
                )}
              </span>
            </div>
          </div>
        </div>
      </section>
    </Dialog.Description>
  )
}

type ScaResultTabsProps = {
  selectedTab: FindingScaDrawerTab
  onTabChange: (value: string) => unknown
  finding: FindingResponseWithoutEmbed
  fixResult: FixResultResponse | undefined
  isFixInProgress: boolean
  onOpenFindingFixDrawer: (findingId: string | undefined, resultId?: string) => unknown
  isActivityTabVisible: boolean
  isFullReportTabVisible: boolean
  isDependencyGraphTabVisible: boolean
  depGraphChains: DependencyChainNode[][] | undefined
  depGraphVerdicts: Map<string, string> | undefined
  activityProps: AnalysisActivityTabProps | undefined
  notification: { status: StatusNotificationStatus; text: string } | undefined
  notificationActionLabel: string | undefined
  onNotificationAction: (() => unknown) | undefined
  outcome: ScaOutcome | null
  scaOutcome: OutcomeBannerOutcome | undefined
  scaArticle: string | undefined
  scaReport: string | undefined
  scaCopyText: string | undefined
  isLive: boolean
  showConfirmation: boolean
  showFixTransitionOverlay: boolean
}

const ScaResultTabs: React.FC<ScaResultTabsProps> = ({
  selectedTab,
  onTabChange,
  finding,
  fixResult,
  isFixInProgress,
  onOpenFindingFixDrawer,
  isActivityTabVisible,
  isFullReportTabVisible,
  isDependencyGraphTabVisible,
  depGraphChains,
  depGraphVerdicts,
  activityProps,
  notification,
  notificationActionLabel,
  onNotificationAction,
  outcome,
  scaOutcome,
  scaArticle,
  scaReport,
  scaCopyText,
  isLive,
  showConfirmation,
  showFixTransitionOverlay,
}) => (
  <Tabs.Root value={selectedTab} onValueChange={onTabChange} className={styles.tabContainer}>
    <ScrollableTabBar
      className={styles.tabList}
      trailingContent={match({
        isFixInProgress,
        fixResult,
        fixOutcomeType: fixResult?.outcome?.type,
      })
        .with({ isFixInProgress: true, fixResult: P.not(P.nullish) }, () => (
          <ProgressButton label="View fix analysis" onClick={() => onOpenFindingFixDrawer(finding.id, fixResult?.id)} />
        ))
        .with({ fixOutcomeType: 'completed-fix' }, () => (
          <GhostButton onClick={() => onOpenFindingFixDrawer(finding.id, fixResult?.id)}>
            <EyeIcon
              size={16}
              weight="fill"
              style={{
                marginRight: '4px',
                color: mappedColors.neutral.halo,
              }}
            />
            <span className={typography.small}>View fix result</span>
          </GhostButton>
        ))
        .with({ fixOutcomeType: 'no-recommendations-fix' }, () => (
          <GhostButton state="disabled">
            <span className={typography.small}>No fix necessary</span>
          </GhostButton>
        ))
        .otherwise(() => undefined)}
    >
      <Tabs.List className={scrollableTabBarStyles.tabList}>
        <Tabs.Trigger className={styles.tab} value="summary">
          SUMMARY
        </Tabs.Trigger>
        {isFullReportTabVisible && (
          <Tabs.Trigger className={styles.tab} value="full-report">
            FULL REPORT
          </Tabs.Trigger>
        )}
        {isActivityTabVisible && (
          <Tabs.Trigger className={styles.tab} value="analysis-activity">
            ANALYSIS ACTIVITY
          </Tabs.Trigger>
        )}
        {isDependencyGraphTabVisible && (
          <Tabs.Trigger className={styles.tab} value="dependency-graph">
            DEPENDENCY GRAPH
          </Tabs.Trigger>
        )}
      </Tabs.List>
    </ScrollableTabBar>

    {notification && (
      <div className={styles.notificationWrapper}>
        <StatusNotification
          status={notification.status}
          text={notification.text}
          actionLabel={notificationActionLabel}
          onAction={onNotificationAction}
        />
      </div>
    )}

    <Tabs.Content className={styles.tabContent} value="summary">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {scaOutcome && (
          <OutcomeBanner
            outcome={scaOutcome}
            copyText={scaCopyText}
            // Blocked/excluded findings have no report — the button copies the reason/details shown.
            copyButtonTooltip={isBlockedOrExcludedScaOutcome(outcome) ? 'Copy summary' : 'Copy full report'}
          />
        )}
        <SummaryTab outcome={outcome} scaArticle={scaArticle} />
      </div>
    </Tabs.Content>
    <Tabs.Content className={styles.tabContent} value="full-report">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {scaOutcome && <OutcomeBanner outcome={scaOutcome} copyText={scaReport} copyButtonTooltip="Copy full report" />}
        <FullReportTab scaReport={scaReport} />
      </div>
    </Tabs.Content>
    {activityProps && (
      <Tabs.Content className={styles.tabContent} value="analysis-activity">
        <AnalysisActivityTab
          {...activityProps}
          progressLabel="SCA analysis"
          isLive={isLive}
          overlay={showConfirmation ? <AnalysisTransitionOverlay message="Rendering SCA result" /> : undefined}
        />
      </Tabs.Content>
    )}
    {isDependencyGraphTabVisible && (
      <Tabs.Content className={styles.tabContent} value="dependency-graph">
        <DependencyGraphTab chains={depGraphChains ?? []} verdicts={depGraphVerdicts} />
      </Tabs.Content>
    )}
    {showFixTransitionOverlay && (
      <AnalysisTransitionOverlay message="Opening fix analysis" ariaLabel="Fix transition" />
    )}
  </Tabs.Root>
)

const SummaryTab: React.FC<{ outcome: ScaOutcome | null; scaArticle?: string }> = ({ outcome, scaArticle }) => {
  if (outcome?.type === 'failed-sca') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div>
          <p className={styles.summaryText}>
            <strong>Error:</strong> {outcome.problem.title || 'Analysis failed'}
          </p>
          {outcome.problem.detail && <p className={styles.summaryText}>{outcome.problem.detail}</p>}
        </div>
      </div>
    )
  }

  // Blocked/excluded findings have no article/report (the endpoints 404), so show the outcome's
  // own reason/details instead of waiting on an article that will never load. Rendered as markdown
  // to match the fix drawer's blocked/excluded handling.
  if (isBlockedOrExcludedScaOutcome(outcome)) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <OutcomeReasonDetails
          reason={outcome.reason}
          details={outcome.details}
          fallback={
            <p className={styles.noSummaryText}>
              This finding was {outcome.type === 'excluded-sca' ? 'excluded from' : 'blocked from'} automatic analysis.
            </p>
          }
        />
      </div>
    )
  }

  if (!outcome) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <p className={styles.noSummaryText}>No SCA analysis available for this finding.</p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {scaArticle ? <RenderedMarkdown markdown={scaArticle} /> : <Spinner label="Loading article" size="64" />}
    </div>
  )
}

const FullReportTab: React.FC<{ scaReport?: string }> = ({ scaReport }) => {
  if (scaReport === undefined) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: '64px',
        }}
      >
        <Spinner label="Loading SCA report" size="128" />
      </div>
    )
  }

  if (scaReport === '') {
    return <p className={styles.noSummaryText}>No SCA report found for this finding.</p>
  }

  return <FullReportSections markdown={scaReport} />
}

const FullReportSections: React.FC<{ markdown: string }> = ({ markdown }) => {
  const sections = parseMarkdownSections(markdown)

  if (sections.length === 0) {
    return <RenderedMarkdown markdown={markdown} />
  }

  const [openSections, setOpenSections] = useState<string[]>(sections.map(section => section.title))

  return (
    <Accordion.Root
      type="multiple"
      value={openSections}
      onValueChange={setOpenSections}
      style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
    >
      {sections.map(section => (
        <Accordion.Item key={section.title} value={section.title}>
          <Accordion.Header>
            <Accordion.Trigger className={styles.collapsibleTrigger} style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ color: mappedColors.neutral.neutral10 }}>{section.title}</span>
                <CaretRightIcon className={styles.caretIcon} size={20} weight="light" />
              </div>
            </Accordion.Trigger>
          </Accordion.Header>
          <Accordion.Content className={styles.collapsibleContent}>
            <div style={{ paddingTop: '16px' }}>
              <RenderedMarkdown markdown={section.content} />
            </div>
          </Accordion.Content>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  )
}

type MarkdownSection = {
  title: string
  content: string
}

const NODE_TYPE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  root: { bg: '#00E79A22', text: '#00E79A', label: 'Root' },
  direct: { bg: '#849cf522', text: '#849cf5', label: 'Direct' },
  transitive: { bg: '#aeabb622', text: '#aeabb6', label: 'Transitive' },
  vulnerable: { bg: '#f6455522', text: '#f64555', label: 'Vulnerable' },
}

const NodeInfoPanel: React.FC<{ node: DependencyNodeDetail }> = ({ node }) => {
  const typeStyle = NODE_TYPE_COLORS[node.node_type] ?? { bg: '#3a335622', text: '#aeabb6', label: node.node_type }
  return (
    <div
      style={{
        background: '#211d31',
        border: '1px solid #312b4a',
        borderRadius: 8,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span
          style={{
            background: typeStyle.bg,
            color: typeStyle.text,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            borderRadius: 4,
            padding: '2px 7px',
          }}
        >
          {typeStyle.label}
        </span>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#fbfafb', fontFamily: 'Inconsolata, monospace' }}>
          {node.name}
          {node.version && <span style={{ fontWeight: 400, color: '#aeabb6' }}>@{node.version}</span>}
        </span>
      </div>

      {node.path.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          {node.path.map((p, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span style={{ color: '#4a4468', fontSize: 11 }}>›</span>}
              <span
                style={{
                  fontSize: 11,
                  color: i === node.path.length - 1 ? '#fbfafb' : '#6b6490',
                  fontFamily: 'Inconsolata, monospace',
                }}
              >
                {p.shortName}
                {p.version ? `@${p.version}` : ''}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {node.version && (
          <div>
            <div
              style={{
                fontSize: 10,
                color: '#6b6490',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: 2,
              }}
            >
              Version
            </div>
            <div style={{ fontSize: 13, color: '#aeabb6', fontFamily: 'Inconsolata, monospace' }}>{node.version}</div>
          </div>
        )}
        <div>
          <div
            style={{
              fontSize: 10,
              color: '#6b6490',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 2,
            }}
          >
            Depth
          </div>
          <div style={{ fontSize: 13, color: '#aeabb6' }}>{node.depth}</div>
        </div>
        <div>
          <div
            style={{
              fontSize: 10,
              color: '#6b6490',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 2,
            }}
          >
            Children
          </div>
          <div style={{ fontSize: 13, color: '#aeabb6' }}>{node.childCount}</div>
        </div>
        <div>
          <div
            style={{
              fontSize: 10,
              color: '#6b6490',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 2,
            }}
          >
            Descendants
          </div>
          <div style={{ fontSize: 13, color: '#aeabb6' }}>{node.descendantCount}</div>
        </div>
      </div>
    </div>
  )
}

const DependencyGraphTab: React.FC<{ chains: DependencyChainNode[][]; verdicts?: Map<string, string> }> = ({
  chains,
  verdicts,
}) => {
  const [selectedNode, setSelectedNode] = useState<DependencyNodeDetail | null>(null)

  if (!chains.length) {
    return (
      <p style={{ fontSize: 13, color: 'var(--color-text-muted, #8b8b8b)', margin: 0 }}>
        Dependency graph data is not available for this finding.
      </p>
    )
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Suspense fallback={<Spinner label="Loading dependency graph" size="64" />}>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted, #8b8b8b)' }}>
          {chains.length} path{chains.length !== 1 ? 's' : ''} · click a node for details · drag to pan · scroll to zoom
        </span>
        <div style={{ background: '#1a1625', borderRadius: 8, overflow: 'hidden' }}>
          <ScaDependencyTree
            chains={chains}
            verdicts={verdicts}
            height={340}
            autoPlay
            interactive
            onNodeClick={setSelectedNode}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1 }}>{selectedNode && <NodeInfoPanel node={selectedNode} />}</div>
          <div style={{ marginTop: 8 }}>
            <ScaDependencyLegend chains={chains} verdicts={verdicts} dotFirst />
          </div>
        </div>
      </Suspense>
    </div>
  )
}

const parseMarkdownSections = (markdown: string): MarkdownSection[] => {
  const lines = markdown.split('\n')

  let highestHeaderLevel = Infinity
  for (const line of lines) {
    const headerMatch = line.match(/^(#{1,6})\s+.+$/)
    if (headerMatch?.[1]) {
      const level = headerMatch[1].length
      if (level < highestHeaderLevel) {
        highestHeaderLevel = level
      }
    }
  }

  if (highestHeaderLevel === Infinity) {
    return []
  }

  const headerPattern = new RegExp(`^#{${highestHeaderLevel}}\\s+(.+)$`)
  const sections: MarkdownSection[] = []
  let currentSection: MarkdownSection | null = null

  for (const line of lines) {
    const headerMatch = line.match(headerPattern)
    if (headerMatch) {
      if (currentSection) {
        sections.push(currentSection)
      }
      currentSection = {
        title: headerMatch[1]?.trim() ?? '',
        content: '',
      }
    } else if (currentSection) {
      currentSection.content += line + '\n'
    }
  }

  if (currentSection) {
    sections.push(currentSection)
  }

  return sections
}

// #endregion
